// istanbul ignore file
import { Theme } from '@mui/system/createTheme/createTheme';

import { BOX_SHADOW_NORMAL } from 'reservations/brand/constants';
import { LIGHT_SOY } from 'reservations/brand/helpers/v1/palette';
import {
  getAddressAndPhoneNumberBrandStyles,
  getAvailabilitiesFormItemBrandStyles,
  getAvailabilityButtonBrandStyles,
  getBookingConfirmationDetailCardBrandStyles,
  getBookingConfirmationDetailHeaderBrandStyles,
  getBookingDetailsCardContentBrandStyles,
  getBookingDetailsCardTextBrandStyles,
  getBookingDetailsCardWrapperBrandStyles,
  getConfirmationDetailsContainerBrandStyles,
  getConfirmationPageWrapperBrandStyles,
  getCustomTipInputBrandStyles,
  getDrawerBrandStyles,
  getLateCancellationPolicyTextBrandStyles,
  getMenuTabButtonBrandStyles,
  getRadioColorBrandStyles,
  getSecondaryButtonBrandStyles,
  getWaitlistButtonTextBrandStyles
} from 'reservations/brand/helpers/v2/styles';

export const getAddressAndPhoneNumberStyles = (
  hasBrandEngineFeature: boolean,
  theme: Theme
) => {
  const baseStyles = {
    color: theme.palette.soy.main,
    fontSize: '14px',
    fontWeight: 400,
    fontStyle: 'normal',
    paddingBottom: '2px'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getAddressAndPhoneNumberBrandStyles(theme)
  };
};

export const getAvailabilitiesFormItemStyles = (
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {};

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getAvailabilitiesFormItemBrandStyles()
  };
};

export const getAvailabilityButtonStyles = (hasBrandEngineFeature: boolean) => {
  const baseStyles = {};

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getAvailabilityButtonBrandStyles()
  };
};

export const getWaitlistButtonTextStyles = (
  theme: Theme,
  hasBrandEngineFeature: boolean,
  isSecondaryText = false
) => {
  let baseStyles = {
    fontWeight: '500',
    color: 'inherit',
    fontSize: 'inherit'
  };

  if (isSecondaryText) {
    baseStyles = {
      ...baseStyles,
      color: theme.palette.soy.dark,
      fontSize: '14px',
      fontWeight: 'light'
    };
  }
  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getWaitlistButtonTextBrandStyles(theme, isSecondaryText)
  };
};

export const getBookingConfirmationDetailCardStyles = (
  theme: Theme,
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {
    background: theme.palette.tofu.main,
    borderRadius: '4px',
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    margin: '8px 0 16px'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getBookingConfirmationDetailCardBrandStyles()
  };
};

export const getBookingConfirmationDetailHeaderStyles = (
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getBookingConfirmationDetailHeaderBrandStyles()
  };
};

export const getConfirmationPageWrapperStyles = (
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {
    padding: 'initial'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getConfirmationPageWrapperBrandStyles()
  };
};

export const getBookingDetailsCardWrapperStyles = (
  hasBrandEngineFeature: boolean,
  additionalStyles?: { [key: string]: string }
) => {
  const baseStyles = {
    borderRadius: '4px',
    boxShadow: BOX_SHADOW_NORMAL,
    p: '8px',
    ...additionalStyles
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getBookingDetailsCardWrapperBrandStyles()
  };
};

export const getBookingDetailsCardContentStyles = (
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {};

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getBookingDetailsCardContentBrandStyles()
  };
};

export const getBookingDetailsCardTextStyles = (
  hasBrandEngineFeature: boolean,
  isBookingCanceled: boolean = false
) => {
  const baseStyles = {
    fontWeight: 500,
    fontSize: '16px'
  };

  if (!hasBrandEngineFeature) {
    return {
      ...baseStyles,
      ...(isBookingCanceled && {
        color: LIGHT_SOY,
        textDecoration: 'line-through'
      })
    };
  }

  return {
    ...baseStyles,
    ...getBookingDetailsCardTextBrandStyles(),
    ...(isBookingCanceled && {
      color: LIGHT_SOY,
      textDecoration: 'line-through'
    })
  };
};

export const getSecondaryButtonStyles = (
  hasBrandEngineFeature: boolean,
  theme?: Theme
) => {
  const baseStyles = {};

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getSecondaryButtonBrandStyles(theme)
  };
};

export const getLateCancellationPolicyTextStyles = (
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {
    fontSize: '16px',
    textAlign: 'center'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getLateCancellationPolicyTextBrandStyles()
  };
};

export const getMenuTabButtonStyles = (hasBrandEngineFeature: boolean) => {
  const baseStyles = {
    borderRadius: 0
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getMenuTabButtonBrandStyles()
  };
};

export const getConfirmationDetailsContainerStyles = (
  hasBrandEngineFeature: boolean
) => {
  const baseStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: 'initial'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getConfirmationDetailsContainerBrandStyles()
  };
};

export const getDrawerStyles = (
  hasBrandEngineFeature: boolean,
  theme: Theme
) => {
  const baseStyles = {
    backgroundColor: '#F1EFEE'
  };

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getDrawerBrandStyles(theme)
  };
};

export const getRadioColorStyles = (
  hasBrandEngineFeature: boolean,
  theme: Theme
) => {
  const baseStyles = {};

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getRadioColorBrandStyles(theme)
  };
};

export const getCustomTipInputStyles = (
  hasBrandEngineFeature: boolean,
  theme: Theme
) => {
  const baseStyles = {};

  if (!hasBrandEngineFeature) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    ...getCustomTipInputBrandStyles(theme)
  };
};
